import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { chatService } from '../services/api';
import { isAuthenticated } from '../services/auth';

const ChatContainer = styled(motion.div)`
  position: ${props => props.fullHeight ? 'relative' : 'absolute'};
  right: ${props => props.fullHeight ? 'auto' : '5%'};
  top: ${props => props.fullHeight ? 'auto' : '30%'};
  transform: ${props => props.fullHeight ? 'none' : 'translateY(-50%)'};
  width: ${props => props.fullHeight ? '100%' : '45%'};
  max-width: ${props => props.fullHeight ? 'none' : '500px'};
  height: ${props => props.fullHeight ? '100%' : props.expanded ? '70vh' : '60px'};
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: height 0.3s ease;
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${props => props.expanded ? '15px' : '0'};
`;

const ChatTitle = styled.h2`
  margin: 0;
  color: white;
  font-size: 1.2rem;
  font-weight: 500;
`;

const ChatMessages = styled(motion.div)`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 15px;
  padding-right: 5px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
  }
`;

const Message = styled(motion.div)`
  padding: 12px 16px;
  border-radius: 18px;
  max-width: 80%;
  word-break: break-word;
  
  ${props => props.isUser ? `
    align-self: flex-end;
    background: rgba(255, 255, 255, 0.25);
    color: white;
    border-bottom-right-radius: 4px;
  ` : `
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.15);
    color: white;
    border-bottom-left-radius: 4px;
  `}
  
  /* Markdown styling */
  h1, h2, h3, h4, h5, h6 {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
    font-weight: 600;
  }
  
  h1 { font-size: 1.4em; }
  h2 { font-size: 1.3em; }
  h3 { font-size: 1.2em; }
  
  p {
    margin-bottom: 0.75em;
  }
  
  ul, ol {
    margin-left: 1.5em;
    margin-bottom: 0.75em;
  }
  
  li {
    margin-bottom: 0.25em;
  }
  
  code {
    background: rgba(255, 255, 255, 0.1);
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: monospace;
  }
  
  pre {
    background: rgba(0, 0, 0, 0.2);
    padding: 0.5em;
    border-radius: 4px;
    overflow-x: auto;
    margin-bottom: 0.75em;
  }
  
  blockquote {
    border-left: 3px solid rgba(255, 255, 255, 0.3);
    padding-left: 1em;
    margin-left: 0;
    margin-right: 0;
    font-style: italic;
  }
  
  a {
    color: #94c2f3;
    text-decoration: underline;
  }
  
  table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: 1em;
  }
  
  th, td {
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 0.4em 0.6em;
    text-align: left;
  }
  
  th {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const InputContainer = styled.div`
  position: relative;
  width: 100%;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.15);
  transition: background 0.3s ease;
  
  &:focus-within {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 30px;
  border: none;
  background: transparent;
  color: white;
  font-size: 1rem;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
`;

const SuggestedText = styled(motion.span)`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.6);
  pointer-events: none;
`;

const SendButton = styled(motion.button)`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// Sample suggested texts that will rotate
const suggestedTexts = [
  "What is the current state of the NHS?",
  "How does the UK tax system work?",
  "Tell me about recent education policy changes",
  "What are the main political parties in the UK?",
  "Explain the process of a bill becoming law",
  "What powers do local councils have?",
  "How is the Prime Minister elected?",
  "What is the role of the House of Lords?",
];

// Sample bot responses (lorem ipsum for prototyping)
const loremResponses = [
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nullam in dui mauris. Vivamus hendrerit arcu sed erat molestie vehicula.",
  "Sed auctor neque eu tellus rhoncus ut eleifend nibh porttitor. Ut in nulla enim. Phasellus molestie magna non est bibendum non venenatis nisl tempor.",
  "Suspendisse dictum feugiat nisl ut dapibus. Mauris iaculis porttitor posuere. Praesent id metus massa, ut blandit odio.",
  "Proin quis tortor orci. Etiam at risus et justo dignissim congue. Donec congue lacinia dui, a porttitor lectus condimentum laoreet.",
  "Nunc eu ullamcorper orci. Quisque eget odio ac lectus vestibulum faucibus eget in metus. In pellentesque faucibus vestibulum.",
];

const ChatWindow = ({ authenticated = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Rotate suggested texts every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSuggestionIndex((prevIndex) => 
        (prevIndex + 1) % suggestedTexts.length
      );
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    // No longer expanding on input
  };

  const handleInputFocus = () => {
    // No longer expanding on focus
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() === '') return;
    
    // Expand the chat window when sending the first message
    if (!expanded) {
      setExpanded(true);
    }
    
    // Create a unique session ID if one doesn't exist
    if (!sessionId) {
      setSessionId(`session_${Date.now()}`);
    }
    
    // Add user message
    const userMessage = { text: inputValue, isUser: true, id: Date.now() };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    
    // Update conversation history
    const updatedHistory = [...conversationHistory, inputValue];
    setConversationHistory(updatedHistory);
    
    // Clear input field
    setInputValue('');
    
    // Show typing indicator
    setIsTyping(true);
    setError(null);
    
    try {
      // Get access token from localStorage if authenticated
      const accessToken = localStorage.getItem('whatgov_access_token');
      
      // Call the chat API
      const response = await chatService.sendMessage(
        inputValue,
        updatedHistory,
        sessionId,
        accessToken
      );
      
      // Extract the response from the Connect protocol format
      // The Connect protocol response is in the format: { response: string, message_id: string }
      const responseData = response;
      
      // Add bot response to messages
      const botMessage = { 
        text: responseData.response, 
        isUser: false, 
        id: responseData.message_id || Date.now() 
      };
      
      setMessages((prevMessages) => [...prevMessages, botMessage]);
      
      // Update conversation history with bot response
      setConversationHistory([...updatedHistory, responseData.response]);
    } catch (err) {
      console.error('Error getting chat response:', err);
      
      // Handle Connect protocol error format if available
      let errorMessage = 'Sorry, there was an error processing your request. Please try again.';
      
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = `Error: ${err.response.data.message}`;
      }
      
      setError(errorMessage);
      
      // Add error message to chat
      const errorMsg = { 
        text: errorMessage, 
        isUser: false, 
        isError: true,
        id: Date.now() 
      };
      
      setMessages((prevMessages) => [...prevMessages, errorMsg]);
    } finally {
      // Hide typing indicator
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const handleSuggestedTextClick = () => {
    // Fill the input with the current suggestion without expanding
    if (!expanded) {
      setInputValue(suggestedTexts[currentSuggestionIndex]);
    }
  };

  // Set expanded state based on authentication
  useEffect(() => {
    if (authenticated) {
      setExpanded(true);
    }
  }, [authenticated]);

  return (
    <ChatContainer 
      expanded={expanded}
      fullHeight={authenticated}
      initial={{ opacity: 0, y: authenticated ? 0 : 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.3 }}
    >
      {expanded && (
        <ChatHeader expanded={expanded}>
          <ChatTitle>Chat</ChatTitle>
        </ChatHeader>
      )}
      
      {expanded && (
        <ChatMessages
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatePresence>
            {messages.map((message) => (
              <Message 
                key={message.id}
                isUser={message.isUser}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                style={message.isError ? { background: 'rgba(255, 100, 100, 0.2)' } : {}}
              >
                {message.isUser ? (
                  message.text
                ) : (
                  <ReactMarkdown>
                    {message.text}
                  </ReactMarkdown>
                )}
              </Message>
            ))}
            
            {isTyping && (
              <Message 
                isUser={false}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ repeat: Infinity, duration: 0.8, repeatType: "reverse" }}
                >
                  Typing...
                </motion.div>
              </Message>
            )}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </ChatMessages>
      )}
      
      <InputContainer>
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={!expanded ? suggestedTexts[currentSuggestionIndex] : ""}
        />
        
        {/* Removed the overlay text in favor of placeholder text */}
        
        {(expanded || inputValue !== '') && (
          <SendButton 
            onClick={handleSendMessage}
            whileTap={{ scale: 0.9 }}
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </SendButton>
        )}
      </InputContainer>
    </ChatContainer>
  );
};

export default ChatWindow;
